import { memo, useEffect, useState } from "react";

import { DataGridPremium } from "@mui/x-data-grid-premium";

import Paper from "@mui/material/Paper";

import { useFindingStore, Finding, FindingSeverity } from "stores/FindingStore";
import { SubmitState } from "stores/lib";

import { GenericObject } from "utility/misc";

import {
  DataGridStyle,
  FindingsColumns,
  FindingsColumnsLoading,
  LoadingRows,
  PaperStyle,
} from "./data";
import Modal from "./FindingModal";

// -----------------------------------------------------------------------------

const PageSize = 20;
const PageSizeOptions = [PageSize];
const NoSort: any[] = [];
const DataComponents: GenericObject = {
  LoadingOverlay: NullComponent,
};

// -----------------------------------------------------------------------------

const OrganizationFindingsDataTable = memo(
  function OrganizationFindingsDataTable(): JSX.Element {
    const [store, findingStoreActions] = useFindingStore();
    const [sort, setSort] = useState<Sort>(undefined as any);

    const [findingToShow, setFindingToShow] = useState(
      undefined as unknown as Finding
    );

    const isLoading = store.loadMetricsState === SubmitState.STARTED;
    const latestFindingToShow = findingToShow
      ? store?.metrics?.findings.find(
          (finding: Finding): boolean => finding.uuid === findingToShow.uuid
        )
      : undefined;
    const [displayFindings, setDisplayFindings] = useState([] as Finding[]);

    function handleSort([
      { field = "", sort: sortDirection = "" } = {},
    ]: any[] = []): void {
      setSort(
        field
          ? {
              field,
              sort: sortDirection,
            }
          : (undefined as any)
      );
    }

    useEffect(() => {
      setDisplayFindings(
        store?.metrics?.findings.filter(outUndesiredFindings) ?? []
      );
    }, [store?.metrics?.findings, setDisplayFindings]);

    useEffect(() => {
      findingStoreActions.loadMetrics();
    }, [store.findingsAll, findingStoreActions]);

    function clearFindingToShow(): void {
      setFindingToShow(undefined as unknown as Finding);
    }

    const onRowClick =
      store.loadMetricsState === SubmitState.SUCCESS
        ? (params: GenericObject): void =>
            setFindingToShow(params.row as Finding)
        : undefined;

    return (
      <Paper elevation={1} sx={PaperStyle}>
        {latestFindingToShow ? (
          <Modal finding={latestFindingToShow} onClose={clearFindingToShow} />
        ) : null}
        <DataGridPremium
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          density="compact"
          getRowId={getRowId}
          rows={isLoading ? LoadingRows : displayFindings}
          rowCount={displayFindings.length}
          columns={isLoading ? FindingsColumnsLoading : FindingsColumns}
          components={DataComponents}
          pageSize={PageSize}
          rowsPerPageOptions={PageSizeOptions}
          checkboxSelection={false}
          sx={DataGridStyle}
          sortModel={sort ? [sort] : NoSort}
          onRowClick={onRowClick}
          onSortModelChange={handleSort}
          loading={isLoading}
        />
      </Paper>
    );
  }
);

export default OrganizationFindingsDataTable;

// -----------------------------------------------------------------------------

function outUndesiredFindings(finding: Finding): boolean {
  return (
    finding.severity === FindingSeverity.Critical ||
    finding.severity === FindingSeverity.High
  );
}

function getRowId(row: GenericObject): string {
  return row.uuid;
}

function NullComponent() {
  return <></>;
}

// -----------------------------------------------------------------------------

interface Sort {
  field: string;
  sort: "asc" | "desc";
}
